import React, { useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { CategoryAvailability, RatePlanOption } from 'api/types/availability.types';
import { formatCurrency } from '../booking-module/booking.utils';

/**
 * Walk-In commercial section — what the guest is charged, chosen as a RATE
 * PACKAGE rather than by tapping rooms.
 *
 * See CRS-CheckIn-GRC-Redesign.md §12. On Walk-In today, picking a room IS the
 * pricing decision: there is no rate selection, no meal plan, and no way to see
 * what a room costs before tapping it. This section is the commercial half of
 * that split — room chips become allocation only, validated against the room
 * count entered here.
 *
 * A "package" is a category plus one of its RatePlanOptions, which already
 * carries rate, meal plan and occupancy — so "Deluxe AC · Standard · AP" needs
 * no new master data, only a label.
 */

export interface WalkInCommercial {
  roomCategoryId: string;
  categoryName: string;
  ratePlanId: string;
  ratePlanDetailId: string;
  ratePlanName: string;
  mealPlanId?: string;
  mealPlanCode: string;
  /**
   * RACK rates, straight from the plan — what gets SAVED on the lines. A
   * negotiated figure never overwrites these; it travels in the agreed* fields
   * below and becomes an adjustment row, the same convention as the booking
   * form's RateAdjustmentFields, so both screens carry one shape.
   */
  ratePerRoom: number;
  mealAdultRate: number;
  mealChildRate: number;
  extraAdultRate: number;
  extraChildRate: number;
  /** Agreed component rates — set only when someone with the permission adjusts. */
  agreedRatePerRoom?: number;
  agreedMealAdultRate?: number;
  agreedExtraAdultRate?: number;
  baseOccupancy: number;
  maxOccupancy: number;
  numberOfRooms: number;
  numAdults: number;
  numChildren: number;
  /** Required by the server once the rate is below rack. */
  rateChangeReason?: string;
  /**
   * Required by the check-in when pax exceed maxOccupancy × rooms (the
   * licensing ceiling, tables/082). Stored on tbl_checkin for compliance.
   */
  occupancyOverrideReason?: string;
}

interface Props {
  categories: CategoryAvailability[];
  value: WalkInCommercial | null;
  onChange: (v: WalkInCommercial | null) => void;
  numNights: number;
  /**
   * Same gate as room-category change: one permission for "may alter what was
   * quoted", rather than a new one needing its own grants, seeds and role updates.
   */
  canEditRate?: boolean;
  disabled?: boolean;
  fieldErrors: Record<string, string>;
}

/** Stable key for a category + rate-plan-detail pair. */
const packageKey = (categoryId: string, ratePlanDetailId: string) =>
  `${categoryId}::${ratePlanDetailId}`;

const WalkInBillingSection: React.FC<Props> = ({
  categories, value, onChange, numNights, canEditRate, disabled, fieldErrors,
}) => {
  /** Adjustment editor visibility — rates read read-only until opened. */
  const [adjustOpen, setAdjustOpen] = React.useState(false);
  /** Every category × rate plan the property can sell tonight. */
  const packages = useMemo(() => {
    const out: { key: string; label: string; cat: CategoryAvailability; rp: RatePlanOption }[] = [];
    for (const cat of categories) {
      for (const rp of cat.ratePlans ?? []) {
        out.push({
          key: packageKey(cat.roomCategoryId, rp.ratePlanDetailId),
          // Rate INCLUSIVE of the meal for base occupancy, matching how the
          // booking screen's rate-plan pill reads — a bare room rate beside a
          // meal-inclusive total is what made figures irreconcilable there.
          label: `${cat.categoryName} · ${rp.rateName}${rp.mealPlanCode ? ` · ${rp.mealPlanCode}` : ''}`
            + ` — ${formatCurrency(rp.ratePerRoom + (rp.mealAdultRate || 0) * (cat.baseOccupancy || 2))}/night`
            + ` (${cat.availableRooms} free)`,
          cat,
          rp,
        });
      }
    }
    return out;
  }, [categories]);

  const selectedKey = value
    ? packageKey(value.roomCategoryId, value.ratePlanDetailId)
    : '';

  const choosePackage = useCallback((key: string) => {
    const p = packages.find(x => x.key === key);
    if (!p) { onChange(null); return; }
    const { cat, rp } = p;
    onChange({
      roomCategoryId: cat.roomCategoryId,
      categoryName: cat.categoryName,
      ratePlanId: rp.ratePlanId,
      ratePlanDetailId: rp.ratePlanDetailId,
      ratePlanName: rp.rateName,
      mealPlanId: rp.mealPlanId || undefined,
      mealPlanCode: rp.mealPlanCode || '',
      ratePerRoom: rp.ratePerRoom,
      mealAdultRate: rp.mealAdultRate || 0,
      mealChildRate: rp.mealChildRate || 0,
      extraAdultRate: rp.extraAdultRate || 0,
      extraChildRate: rp.extraChildRate || 0,
      baseOccupancy: cat.baseOccupancy || 2,
      maxOccupancy: cat.maxOccupancy || 2,
      // Sensible start: one room at its normal selling occupancy. The operator
      // adjusts; nothing is derived behind their back afterwards.
      numberOfRooms: value?.numberOfRooms || 1,
      numAdults: value?.numAdults || (cat.baseOccupancy || 2),
      numChildren: value?.numChildren || 0,
    });
  }, [packages, onChange, value]);

  const patch = useCallback((p: Partial<WalkInCommercial>) => {
    if (!value) return;
    onChange({ ...value, ...p });
  }, [value, onChange]);

  const totals = useMemo(() => {
    if (!value) return null;
    // Effective = agreed where set, rack otherwise — what the guest pays.
    const roomRate = value.agreedRatePerRoom ?? value.ratePerRoom;
    const mealAdult = value.agreedMealAdultRate ?? value.mealAdultRate;
    const extraAdult = value.agreedExtraAdultRate ?? value.extraAdultRate;
    const rooms = value.numberOfRooms || 0;
    const room = roomRate * rooms * numNights;
    const meal = (mealAdult * value.numAdults
      + value.mealChildRate * value.numChildren) * numNights;
    const capacity = value.baseOccupancy * rooms;
    const extraAdults = Math.max(0, value.numAdults - capacity);
    const extra = extraAdults * extraAdult * numNights;
    return { room, meal, extra, total: room + meal + extra };
  }, [value, numNights]);

  const overCapacity = value
    ? (value.numAdults + value.numChildren) > value.maxOccupancy * (value.numberOfRooms || 0)
    : false;

  /** Any component adjusted at all (premium or concession). */
  const hasAdjustment = !!value && (
    (value.agreedRatePerRoom != null && value.agreedRatePerRoom !== value.ratePerRoom)
    || (value.agreedMealAdultRate != null && value.agreedMealAdultRate !== value.mealAdultRate)
    || (value.agreedExtraAdultRate != null && value.agreedExtraAdultRate !== value.extraAdultRate)
  );

  return (
    <div>
      <div className="booking-section-title">
        <IconSystem.tag size={15} />
        Rate &amp; Charges
        <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          Choose the package first — rooms are allocated after
        </span>
      </div>

      <div className="form-group">
        <label className="form-label">Rate package <span className="text-danger">*</span></label>
        <select
          className={`form-input${fieldErrors['package'] ? ' form-field-error' : ''}`}
          value={selectedKey}
          onChange={(e) => choosePackage(e.target.value)}
          disabled={disabled}
        >
          <option value="">Select a package…</option>
          {packages.map(p => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>
        {fieldErrors['package'] && <div className="form-error-message">{fieldErrors['package']}</div>}
      </div>

      {value && (
        <>
          <div className="form-grid-3">
            <div className="form-group">
              <label className="form-label">Rooms <span className="text-danger">*</span></label>
              <input
                type="number" min={1} className="form-input"
                value={value.numberOfRooms}
                onChange={(e) => patch({ numberOfRooms: Math.max(1, Number(e.target.value) || 1) })}
                disabled={disabled}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Adults</label>
              <input
                type="number" min={0} className="form-input"
                value={value.numAdults}
                onChange={(e) => patch({ numAdults: Math.max(0, Number(e.target.value) || 0) })}
                disabled={disabled}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Children</label>
              <input
                type="number" min={0} className="form-input"
                value={value.numChildren}
                onChange={(e) => patch({ numChildren: Math.max(0, Number(e.target.value) || 0) })}
                disabled={disabled}
              />
            </div>
          </div>
          {fieldErrors['commercialPax'] && (
            <div className="form-error-message" style={{ marginTop: '-0.4rem', marginBottom: '0.5rem' }}>
              {fieldErrors['commercialPax']}
            </div>
          )}

          {/* Occupancy guidance — warn, never block. A hard stop pushes staff to
              under-report pax to get the guest into a room, which destroys the
              number the rule exists to protect. */}
          {overCapacity && (
            <>
              <div className="wi-warn">
                ⚠ {value.numAdults + value.numChildren} guests in {value.numberOfRooms}{' '}
                {value.categoryName} room{value.numberOfRooms !== 1 ? 's' : ''} exceeds the
                category maximum of {value.maxOccupancy} per room
                (normal {value.baseOccupancy}). Allowed — check the occupancy licence.
              </div>
              {/* The check-in server refuses an over-ceiling arrival without a
                  recorded reason (tables/082) — captured here, beside the
                  advisory it answers, and sent with the check-in call. */}
              <div className="form-group" data-field="occupancyOverrideReason">
                <label className="form-label">
                  Over-occupancy reason <span className="text-danger">*</span>
                </label>
                <input
                  type="text" maxLength={200}
                  className={`form-input${fieldErrors['occupancyOverrideReason'] ? ' form-field-error' : ''}`}
                  value={value.occupancyOverrideReason ?? ''}
                  placeholder="e.g. Extra mattress provided — family with children"
                  onChange={(e) => patch({ occupancyOverrideReason: e.target.value })}
                  disabled={disabled}
                />
                {fieldErrors['occupancyOverrideReason'] && (
                  <div className="form-error-message">{fieldErrors['occupancyOverrideReason']}</div>
                )}
              </div>
            </>
          )}

          {/* ── Rates: read-only until deliberately adjusted. An always-editable
                 rate invites accidental overwrites of the quoted figure; the
                 booking form settled this as rack-shown, agreed-entered,
                 component-wise — mirrored here so the desk learns one pattern. ── */}
          <div className="wi-rate-line">
            <span>
              Rate <b>{formatCurrency(value.agreedRatePerRoom ?? value.ratePerRoom)}</b>/room/night
              {value.mealPlanCode && <span className="wi-plan"> · {value.mealPlanCode}</span>}
              {hasAdjustment && (
                <span className="wi-delta" style={{ display: 'inline', marginLeft: '0.4rem' }}>
                  adjusted · rack {formatCurrency(value.ratePerRoom)}
                </span>
              )}
            </span>
            {canEditRate && (
              <button
                type="button"
                className="wi-adjust-btn"
                onClick={() => setAdjustOpen((o) => !o)}
                disabled={disabled}
              >
                {adjustOpen ? 'Hide rate adjustment' : 'Adjust rates'}
              </button>
            )}
          </div>

          {/* Set when the effective room rate is adjusted to zero — must render
              here since the block that used to show it was removed. */}
          {fieldErrors['rate'] && (
            <div className="form-error-message" style={{ marginTop: '-0.3rem', marginBottom: '0.5rem' }}>
              {fieldErrors['rate']}
            </div>
          )}

          {canEditRate && adjustOpen && (
            <div className="wi-adjust">
              {([
                ['Room Tariff', 'room-night', value.ratePerRoom, value.agreedRatePerRoom,
                  (v?: number) => patch({ agreedRatePerRoom: v })],
                ['Meal Plan', 'adult-night', value.mealAdultRate, value.agreedMealAdultRate,
                  (v?: number) => patch({ agreedMealAdultRate: v })],
                ['Extra Person', 'person-night', value.extraAdultRate, value.agreedExtraAdultRate,
                  (v?: number) => patch({ agreedExtraAdultRate: v })],
              ] as Array<[string, string, number, number | undefined, (v?: number) => void]>).map(
                ([label, unit, rack, agreed, set]) => (
                  <div className="wi-adjust-row" key={label}>
                    <span className="wi-adjust-label">
                      {label} <span className="wi-mut">/ {unit}</span>
                    </span>
                    <span className="wi-adjust-rack">{formatCurrency(rack)}</span>
                    <input
                      type="number" min={0} step="0.01"
                      className={`form-input wi-adjust-input${agreed != null && agreed !== rack ? ' wi-edited' : ''}`}
                      value={agreed ?? rack}
                      onChange={(e) => {
                        const n = Math.max(0, Number(e.target.value) || 0);
                        // Typing the rack back clears the adjustment entirely —
                        // an agreed value equal to rack is noise on the record.
                        set(n === rack ? undefined : n);
                      }}
                      disabled={disabled}
                    />
                  </div>
                ),
              )}
              <input
                type="text" maxLength={200}
                className={`form-input${fieldErrors['rateChangeReason'] ? ' form-field-error' : ''}`}
                value={value.rateChangeReason ?? ''}
                onChange={(e) => patch({ rateChangeReason: e.target.value })}
                placeholder="Reason — required for a concession (audit / approval)"
                disabled={disabled}
              />
              {fieldErrors['rateChangeReason'] && (
                <div className="form-error-message">{fieldErrors['rateChangeReason']}</div>
              )}
              <div className="wi-mut" style={{ fontSize: '0.68rem' }}>
                Rack rates stay on record; only the difference is stored, so Rooms / F&amp;B
                revenue stays department-accurate.
              </div>
            </div>
          )}

          {totals && (
            <div className="wi-totals">
              <span>
                {value.numberOfRooms} room{value.numberOfRooms !== 1 ? 's' : ''} ×{' '}
                {numNights} night{numNights !== 1 ? 's' : ''} @ {formatCurrency(value.agreedRatePerRoom ?? value.ratePerRoom)}
                {value.mealPlanCode && <span className="wi-plan"> · {value.mealPlanCode}</span>}
              </span>
              <span className="wi-breakdown">
                Room {formatCurrency(totals.room)}
                {totals.meal > 0 && <> · Meals {formatCurrency(totals.meal)}</>}
                {totals.extra > 0 && <> · Extra pax {formatCurrency(totals.extra)}</>}
              </span>
              <span className="wi-total">{formatCurrency(totals.total)}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WalkInBillingSection;
